import { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { errorMessages } from '~/constants/errorMessages.ts';
import type { TimerRefHandle } from '~/components/SignatureVerification/DiiaQRCode.tsx';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import { useFilesSigning } from '~/modules/guarantees/form/application/shared/filesSigning/useFilesSigning.ts';
import { buildGuaranteesCounterpartyPath } from '~/modules/guarantees/utils.ts';
import {
  fetchGuaranteesApplication,
  submitGuaranteesApplicationByClient,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import { selectIIT } from '~/store/iit/selectors.ts';
import type { AppDispatch } from '~/store/store.ts';
import type {
  TFilesSigningSaveSignedDocumentsResponse,
  TGuaranteesApplicationFile,
} from '~/store/guarantees/types.ts';
import { CLIENT_STEP2_TEXT } from '../constants.ts';
import { buildClientStep2SigningDocuments } from '../utils.ts';
import type { TGuaranteesModalSuccessState } from '../../../types.ts';

export const useClientStep2Signing = ({
  applicationId,
  applicationFiles,
  contragentId,
  hasRequiredFoundingDocument,
  setModalAlert,
  setModalSuccess,
  showGuaranteesRequestError,
}: {
  applicationId: string;
  applicationFiles: TGuaranteesApplicationFile[];
  contragentId: string;
  hasRequiredFoundingDocument: boolean;
  setModalAlert: (value: { text: string }) => void;
  setModalSuccess: (value: TGuaranteesModalSuccessState) => void;
  showGuaranteesRequestError: (error: unknown) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isLoading: isIITLoading } = useSelector(selectIIT) || {};
  const timerRef = useRef<TimerRefHandle>(null);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isSuccessRedirectLocked, setIsSuccessRedirectLocked] = useState(false);
  const [deepLink, setDeepLink] = useState('');
  const { fileIds, documents } = useMemo(
    () => buildClientStep2SigningDocuments(applicationFiles),
    [applicationFiles]
  );

  const getActualApplicationVersion = async (
    saveResponse?: TFilesSigningSaveSignedDocumentsResponse
  ) => {
    if (typeof saveResponse?.version === 'number') {
      return saveResponse.version;
    }

    if (!applicationId || applicationId === 'new' || !contragentId) {
      return null;
    }

    const refreshedApplication = await dispatch(
      fetchGuaranteesApplication({ contragentId, applicationId })
    ).unwrap();

    return typeof refreshedApplication?.version === 'number'
      ? refreshedApplication.version
      : null;
  };

  const onSigningSuccess = async (saveResponse?: TFilesSigningSaveSignedDocumentsResponse) => {
    setIsSignatureModalOpen(false);
    setIsSuccessRedirectLocked(true);

    let version: number | null = null;

    try {
      version = await getActualApplicationVersion(saveResponse);
    } catch (error) {
      setIsSuccessRedirectLocked(false);
      showGuaranteesRequestError(error);

      return;
    }

    if (!applicationId || applicationId === 'new' || typeof version !== 'number') {
      setIsSuccessRedirectLocked(false);
      showGuaranteesRequestError({ message: errorMessages.SERVICE_UNAVAILABLE });

      return;
    }

    try {
      await dispatch(
        submitGuaranteesApplicationByClient({
          applicationId,
          version,
        })
      ).unwrap();
    } catch (error) {
      setIsSuccessRedirectLocked(false);
      showGuaranteesRequestError(error);

      return;
    }

    const navigateToApplications = () => {
      setIsSuccessRedirectLocked(false);
      navigate(buildGuaranteesCounterpartyPath(contragentId, false), { replace: true });
    };

    setModalSuccess({
      text: CLIENT_STEP2_TEXT.signSuccessMessage,
      buttonValue: guaranteesClientText.backToApplicationsButton,
      onClick: navigateToApplications,
      onClose: navigateToApplications,
    });
  };

  const onSigningError = (message: string) => {
    setIsSignatureModalOpen(false);
    setModalAlert({ text: message });
  };

  const { handleIITSignature, onIITSign, handleDiiaSignature, resetSigningSession } =
    useFilesSigning({
      fileIds,
      applicationId,
      onSuccess: onSigningSuccess,
      onError: onSigningError,
      setDeepLink,
      timerRef,
    });

  const handleSign = () => {
    if (!hasRequiredFoundingDocument) {
      setModalAlert({ text: CLIENT_STEP2_TEXT.requiredFoundingDocumentMessage });
      return;
    }

    if (!fileIds.length) {
      setModalAlert({ text: errorMessages.NO_FILES_TO_SIGN });
      return;
    }

    setIsSignatureModalOpen(true);
  };

  const handleCloseSignatureModal = () => {
    setIsSignatureModalOpen(false);
    resetSigningSession();
  };

  const handleTimeUp = () => {
    resetSigningSession();
    showGuaranteesRequestError({ message: errorMessages.SIGNATURE_QR_CODE_TIME_OUT });
    setIsSignatureModalOpen(false);
  };

  return {
    deepLink,
    documents,
    isSignatureModalOpen,
    isSuccessRedirectLocked,
    isSigningLoading: isIITLoading,
    timerRef,
    handleSign,
    handleCloseSignatureModal,
    handleDiiaSignature,
    handleIITSignature,
    handleTimeUp,
    onIITSign,
  };
};
